import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Pagination,
  Alert,
  Snackbar,
  Button,
  Chip,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import axios from 'axios';
import config from '../config/config.json';
import { useAuth } from '../context/AuthContext';
import UserList from '../components/Users/UserList';

function UsersPage() {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  useEffect(() => {
    const query = searchParams.get('search') || '';
    setSearch(query);
    setSearchInput(query);
    setPage(1);
  }, [searchParams]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.api_base}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 20, search }
      });

      if (response.data.statusCode === 200) {
        setUsers(response.data.data.users);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalUsers(response.data.data.pagination.total || 0);
      }
    } catch (error) {
      console.error('Users fetch error:', error);
      showSnackbar('Kullanıcılar yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const response = await axios.patch(
        `${config.api_base}/api/admin/users/${userId}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.statusCode === 200) {
        showSnackbar(response.data.message, 'success');
        fetchUsers();
      }
    } catch (error) {
      console.error('Toggle active error:', error);
      showSnackbar('İşlem sırasında hata oluştu', 'error');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${config.api_base}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.statusCode === 200) {
        showSnackbar(response.data.message, 'success');
        fetchUsers();
      }
    } catch (error) {
      console.error('Delete user error:', error);
      showSnackbar('Kullanıcı silinirken hata oluştu', 'error');
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const activeUsersOnPage = users.filter(user => user.isActive !== false).length;
  const blockedUsersOnPage = users.length - activeUsersOnPage;
  const proUsersOnPage = users.filter((user) => user.subscription?.type === 'pro').length;
  const standardUsersOnPage = users.filter((user) => user.subscription?.type === 'standard').length;
  const hasSearch = Boolean(search);
  const pageStart = totalUsers === 0 ? 0 : (page - 1) * 20 + 1;
  const pageEnd = totalUsers === 0 ? 0 : Math.min(page * 20, totalUsers);

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E7EB'
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontSize: '30px',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '8px'
              }}
            >
              Kullanıcı Yönetimi
            </Typography>
            <Typography sx={{ color: '#6B7280', maxWidth: 620 }}>
              Kullanıcıları ara, hesap durumlarını incele ve yönetim işlemlerini kontrollü şekilde uygula.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<PeopleAltOutlinedIcon />}
              label={`${totalUsers} toplam kayıt`}
              sx={{
                backgroundColor: '#EFF6FF',
                color: '#2563EB',
                fontWeight: 600,
                borderRadius: '10px',
                height: 36
              }}
            />
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label={`${activeUsersOnPage} aktif / sayfa`}
              sx={{
                backgroundColor: '#ECFDF5',
                color: '#059669',
                fontWeight: 600,
                borderRadius: '10px',
                height: 36
              }}
            />
            <Chip
              icon={<TravelExploreOutlinedIcon />}
              label={`Sayfa ${page} / ${totalPages}`}
              sx={{
                backgroundColor: '#F8FAFC',
                color: '#475569',
                fontWeight: 600,
                borderRadius: '10px',
                height: 36
              }}
            />
            <Chip
              icon={<WorkspacePremiumOutlinedIcon />}
              label={`${proUsersOnPage} Pro · ${standardUsersOnPage} Standart`}
              sx={{
                backgroundColor: '#F5F3FF',
                color: '#7C3AED',
                fontWeight: 600,
                borderRadius: '10px',
                height: 36
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <Box
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E7EB'
        }}
      >
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <TextField
            fullWidth
            placeholder="Email, isim veya kullanıcı adı ile ara..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#F8FAFC',
                borderRadius: '12px'
              }
            }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{
                minWidth: 140,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none'
              }}
            >
              Ara
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              disabled={!search && !searchInput}
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setPage(1);
              }}
              sx={{
                minWidth: 140,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Temizle
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ mt: 2.5 }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {hasSearch ? (
              <Chip
                label={`Arama: ${search}`}
                onDelete={() => {
                  setSearch('');
                  setSearchInput('');
                  setPage(1);
                }}
                sx={{
                  backgroundColor: '#EFF6FF',
                  color: '#2563EB',
                  borderRadius: '10px',
                  fontWeight: 600
                }}
              />
            ) : (
              <Chip
                label="Tüm kullanıcılar"
                sx={{
                  backgroundColor: '#F8FAFC',
                  color: '#475569',
                  borderRadius: '10px',
                  fontWeight: 600
                }}
              />
            )}
            <Chip
              label={`${blockedUsersOnPage} engelli / sayfa`}
              sx={{
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                borderRadius: '10px',
                fontWeight: 600
              }}
            />
          </Stack>

          <Typography sx={{ color: '#6B7280', fontSize: '14px', fontWeight: 500 }}>
            {pageStart}-{pageEnd} / {totalUsers} kayıt gösteriliyor
          </Typography>
        </Stack>
      </Box>

      <UserList
        users={users}
        onToggleActive={handleToggleActive}
        onDelete={handleDelete}
        totalUsers={totalUsers}
        hasSearch={hasSearch}
      />

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UsersPage;
